/** Returns 15 if costCents < thresholdCents (default 150 000), else 10. */
export function calculateDefaultMargin(
  costCents: number,
  thresholdCents = 150_000,
): number {
  return costCents < thresholdCents ? 15 : 10;
}

/** Calculate price from cost and margin percentage. */
export function priceFromMargin(costCents: number, marginPct: number): number {
  if (marginPct >= 100) return costCents * 10; // guard
  return Math.round(costCents / (1 - marginPct / 100));
}

/** Calculate margin percentage from cost and price. */
export function marginFromPrice(costCents: number, priceCents: number): number {
  if (priceCents <= 0) return 0;
  return Math.round(((priceCents - costCents) / priceCents) * 100 * 10) / 10;
}

/** Rev4: (pvp - cost) / pvp * 100, rounded to 1 decimal. */
export function rev4(pvpCents: number, costCents: number): number {
  if (pvpCents <= 0) return 0;
  return Math.round(((pvpCents - costCents) / pvpCents) * 100 * 10) / 10;
}

export interface Rev4Item {
  pvp_cents: number;
  cost_cents: number;
  /** Rev3 unit price snapshot (cents). Optional for backwards compatibility. */
  rev3_cents?: number;
  /** Rev4 unit price snapshot (cents). Optional — falls back to rev3 then cost
   *  when computing REV4-mode references. */
  rev4_cents?: number;
  quantity: number;
}

/** F2-VR + PR 6a — Reference type used to compute margin and validate
 *  minimum price.
 *  - REV3: reseller list price snapshot (default for new revisions).
 *  - COST: catalog cost (admin-only, or legacy fallback).
 *  - REV4: reseller Rev4 unit price snapshot (any role; falls back to REV3
 *    then COST when rev4_cents is absent on an item). */
export type ReferenceType = "REV3" | "COST" | "REV4";

/** Exhaustive narrowing helper. Compile-time error if `ReferenceType` gains
 *  a new case that is not handled by the caller's switch. */
function assertNever(x: never): never {
  throw new Error(`Unhandled reference type: ${String(x)}`);
}

/** Per-unit reference value in cents for a given item under a reference type.
 *  - REV4 falls back to rev3_cents, then cost_cents (legacy/missing snapshots).
 *  - REV3 falls back to cost_cents (legacy items pre F2-VR).
 *  - COST is always cost_cents. */
export function referenceUnitCents(
  item: Pick<Rev4Item, "cost_cents" | "rev3_cents" | "rev4_cents">,
  type: ReferenceType,
): number {
  switch (type) {
    case "REV4":
      return item.rev4_cents ?? item.rev3_cents ?? item.cost_cents;
    case "REV3":
      return item.rev3_cents ?? item.cost_cents;
    case "COST":
      return item.cost_cents;
    default:
      return assertNever(type);
  }
}

/** Sum of (referenceUnitCents × quantity) across items under a reference type. */
export function totalReferenceCents(
  items: Rev4Item[],
  type: ReferenceType,
): number {
  return items.reduce(
    (acc, item) => acc + referenceUnitCents(item, type) * item.quantity,
    0,
  );
}

/** Absolute margin in cents = priceCents − referenceCents.
 *  Negative values indicate selling below the reference (invalid in REV3 mode). */
export function marginAbsCents(
  priceCents: number,
  referenceCents: number,
): number {
  return priceCents - referenceCents;
}

// ── PR 6a — KPI helpers ─────────────────────────────────────────────────────

/** Markup ratio: `(unit − cost) / cost`. Returns `undefined` when `cost` is
 *  missing, zero, or negative (defensive). Used by the admin KPI block for
 *  `realGrossMarginPct`. Returns negative values when selling below cost. */
export function computeMarkup(
  unitCents: number,
  costCents: number | undefined,
): number | undefined {
  if (costCents === undefined || costCents <= 0) return undefined;
  return (unitCents - costCents) / costCents;
}

/** Delta over the Rev3 reference: `(unit − rev3) / rev3`. Returns `undefined`
 *  when `rev3` is missing, zero, or negative. NEVER clamps to 0 — negative
 *  values are valid and signal undercutting the reseller list price. */
export function computeAboveRev3Pct(
  unitCents: number,
  rev3Cents: number | undefined,
): number | undefined {
  if (rev3Cents === undefined || rev3Cents <= 0) return undefined;
  return (unitCents - rev3Cents) / rev3Cents;
}

/** Weighted average rev4 across items (weighted by quantity). */
export function rev4Avg(items: Rev4Item[]): number {
  const total = items.reduce((s, i) => s + i.quantity, 0);
  if (total === 0) return 0;
  const weighted = items.reduce(
    (s, i) => s + rev4(i.pvp_cents, i.cost_cents) * i.quantity,
    0,
  );
  return Math.round((weighted / total) * 10) / 10;
}

/**
 * Semantic margin zone based on margin percentage.
 *
 * - loss:    margin < 0   (selling below cost)
 * - low:     0 ≤ m < 5
 * - healthy: 5 ≤ m < 25
 * - high:    m ≥ 25
 */
export type MarginZone = "loss" | "low" | "healthy" | "high";

export function marginZone(marginPct: number): MarginZone {
  if (marginPct < 0) return "loss";
  if (marginPct < 5) return "low";
  if (marginPct < 25) return "healthy";
  return "high";
}

/**
 * Distributes a new final total price across items proportionally by their
 * current pvp_cents contribution, preserving cost_cents on each item.
 *
 * The last item absorbs any rounding remainder so the sum is exact.
 */
export function distributeFinalPrice<
  T extends { pvp_cents: number; cost_cents: number; quantity: number },
>(items: T[], finalTotalCents: number): T[] {
  if (items.length === 0) return items;

  const currentTotal = items.reduce((s, i) => s + i.pvp_cents * i.quantity, 0);

  if (currentTotal === 0) {
    // Distribute evenly when all prices are 0.
    const perUnit = Math.round(
      finalTotalCents / items.reduce((s, i) => s + i.quantity, 0),
    );
    return items.map((item) => ({ ...item, pvp_cents: perUnit }));
  }

  let allocated = 0;
  const result = items.map((item, idx) => {
    const isLast = idx === items.length - 1;
    const itemTotal = item.pvp_cents * item.quantity;
    const share = isLast
      ? finalTotalCents - allocated
      : Math.round((itemTotal / currentTotal) * finalTotalCents);
    allocated += share;
    const pvp_cents = Math.max(
      item.cost_cents,
      Math.round(share / item.quantity),
    );
    return { ...item, pvp_cents };
  });

  return result;
}
